import {
  Course,
  CourseCategory,
  CourseRecommendation,
  CourseStatus,
  FullLesson,
  RolePlay,
} from '@agently/types';
import { createMutation, createSuspenseQuery } from 'react-query-kit';

import { api } from './client';

export const useUploadFile = createMutation({
  mutationFn: async ({
    courseId,
    file,
    lessonId,
  }: {
    courseId: number;
    file: File;
    lessonId: number;
  }) => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await api.post<{ id: number; url: string }>(
      `/lms/courses/${courseId}/lessons/${lessonId}/upload`,
      formData,
    );
    return data;
  },
});

export const useGetLesson = createSuspenseQuery({
  fetcher: async ({
    courseId,
    lessonId,
  }: {
    courseId: number;
    lessonId: number;
  }) => {
    const { data } = await api.get<FullLesson>(
      `/lms/courses/${courseId}/lessons/${lessonId}`,
    );
    return data;
  },
  queryKey: ['lessons'],
});

export const useCompleteLesson = createMutation({
  mutationFn: ({
    body,
    courseId,
    lessonId,
  }: {
    body?: any;
    courseId: number;
    lessonId: number;
  }) =>
    api.post<null>(
      `/lms/courses/${courseId}/lessons/${lessonId}/complete`,
      body || {},
    ),
});

export const useGetCourses = createSuspenseQuery({
  fetcher: async ({ status }: { status?: CourseStatus[] } | undefined = {}) => {
    const { data } = await api.get<{ courses: Course[] }>('/lms/courses', {
      params: { status },
    });
    return data.courses;
  },
  queryKey: ['courses'],
});

export const useGetCourse = createSuspenseQuery({
  fetcher: async ({ courseId }: { courseId: number }) => {
    const { data } = await api.get<Course>(`/lms/courses/${courseId}`);
    return data;
  },
  queryKey: ['courses'],
});

export const useStartCourse = createMutation({
  mutationFn: async ({ courseId }: { courseId: number }) => {
    const { data } = await api.post<Course>(`/lms/courses/${courseId}/start`);
    return data;
  },
});

export const useGetCourseCategories = createSuspenseQuery({
  fetcher: async () => {
    const { data } = await api.get<{
      categories: CourseCategory[];
    }>('/lms/courses/categories');
    return data.categories;
  },
  queryKey: ['courses', 'categories'],
});

export const useCoursesRecommendations = createSuspenseQuery({
  fetcher: async () => {
    const { data } = await api.get<{
      courses: CourseRecommendation[];
    }>('/lms/courses/recommendations');

    return data.courses;
  },
  queryKey: ['courses', 'recommendations'],
});

export const useCompleteRolePlay = createMutation({
  mutationFn: async ({
    answerAudioUrl,
    courseId,
    lessonId,
    roleplayId,
  }: {
    answerAudioUrl: string;
    courseId: number;
    lessonId: number;
    roleplayId: number;
  }) => {
    const { data } = await api.post<null>(
      `/lms/courses/${courseId}/lessons/${lessonId}/roleplays/${roleplayId}/complete`,
      {
        answerAudioUrl,
      },
    );
    return data;
  },
});

export const useUploadRoleplay = createMutation({
  mutationFn: async ({
    courseId,
    file,
    lessonId,
  }: {
    courseId: number;
    file: File;
    lessonId: number;
  }) => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await api.post<{ url: string }>(
      `/lms/courses/${courseId}/lessons/${lessonId}/roleplays/upload`,
      formData,
    );
    return data;
  },
});

export const useRoleplay = createSuspenseQuery({
  fetcher: async ({
    lessonId,
    roleplayId,
  }: {
    lessonId: number;
    roleplayId: number;
  }) => {
    const { data } = await api.get<RolePlay>(
      `/lms/courses/lessons/${lessonId}/roleplays/${roleplayId}`,
    );
    return data;
  },
  queryKey: ['lessons', 'roleplays'],
});
